import "./style.scss";

import { TabContext, TabPanel } from "@mui/lab";
import { PortalType } from "contexts/portal.context";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
// Imports from store
import { getCompanyList } from "store/adminPanelSlice";
import { useAppDispatch, useAppSelector } from "store/store";

import CompaniesListPanel from "./TabPanels/CompaniesList";
import CompanyGroupsPanel from "./TabPanels/CompanyGroups";
import CompanyHiddenSettings from "./TabPanels/CompanyHiddenSettings";
import MaintenanceSchedule from "./TabPanels/MaintenanceSchedule";
import CreateCompanyPanel from "./TabPanels/CreateCompany";
import CreateUserPanel from "./TabPanels/CreateUser";
import DangerousSettings from "./TabPanels/DangerousSettings";
import ImportTracking from "./TabPanels/ImportTracking";
import InterforumFiles from "./TabPanels/InterforumFiles";
import LogList from "./TabPanels/LogList";
import StatementI18n from "./TabPanels/StatementI18n";
import UserListPanel from "./TabPanels/UserList";

type TProps = {
    tabs:
        | "createCompany"
        | "createUser"
        | "companySwitch"
        | "userList"
        | "companiesList"
        | "companyGroups"
        | "companyHiddenSettings"
        | "maintenance"
        | "dangerousSettings"
        | "logList"
        | "processingJobList"
        | "statementTranslation"
        | "interforumFiles";
};
const AdminPanel = () => {
    const { tabName = "createUser" } = useParams<{ tabName: TProps["tabs"] }>();
    const { count: usersCount } = useAppSelector((state) => state.adminPanel.adminPortalUsersPaginated);
    const { data: companies } = useAppSelector((state) => state.adminPanel.companies);
    const navigate = useNavigate();
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const maintenanceLocked = useAppSelector(
        (state) => state.appMaintenance.unavailable && state.appMaintenance.bypassed,
    );
    const maintenanceTabOpen = (tab: TProps["tabs"]) => tab === "maintenance";
    const openAdminTab = (tab: TProps["tabs"]) => {
        if (maintenanceLocked && !maintenanceTabOpen(tab)) return;
        navigate(`/admin-panel/${tab}`);
    };
    const adminTabClass = (tab: TProps["tabs"]) => {
        const active = tabName === tab ? " active" : "";
        const disabled = maintenanceLocked && !maintenanceTabOpen(tab) ? " is-disabled" : "";
        return `admin-settings-navbar-link${active}${disabled}`;
    };

    useEffect(() => {
        dispatch(getCompanyList());
    }, [dispatch]);

    return (
        <div className="admin-panel-settings-page">
            <div className="admin-settings-navbar">
                <h2>{t("AdminPanel.AdminPanel")}</h2>
                <ul className="admin-settings-navbar-list">
                    <div>
                        <h3>{t("AdminPanel.General")}</h3>
                        <li className={adminTabClass("createCompany")} onClick={() => openAdminTab("createCompany")}>
                            {t("AdminPanel.CreateCompany")}
                        </li>
                        <li className={adminTabClass("userList")} onClick={() => openAdminTab("userList")}>
                            {t("AdminPanel.ListUsers")}
                        </li>
                        <li className={adminTabClass("companiesList")} onClick={() => openAdminTab("companiesList")}>
                            {t("AdminPanel.ListCompanies")}
                        </li>
                        <li className={adminTabClass("companyGroups")} onClick={() => openAdminTab("companyGroups")}>
                            {t("AdminPanel.CompanyGroups")}
                        </li>
                        <li
                            className={adminTabClass("companyHiddenSettings")}
                            onClick={() => openAdminTab("companyHiddenSettings")}
                        >
                            {t("AdminPanel.HiddenSettings")}
                        </li>
                        <li className={adminTabClass("maintenance")} onClick={() => openAdminTab("maintenance")}>
                            {t("AdminPanel.Maintenance")}
                        </li>
                        <li
                            className={adminTabClass("statementTranslation")}
                            onClick={() => openAdminTab("statementTranslation")}
                        >
                            {t("AdminPanel.StatementI18n")}
                        </li>
                        <li
                            className={adminTabClass("dangerousSettings")}
                            onClick={() => openAdminTab("dangerousSettings")}
                        >
                            {t("AdminPanel.DangerousSettings")}
                        </li>
                        <li className={adminTabClass("logList")} onClick={() => openAdminTab("logList")}>
                            {t("AdminPanel.LogList")}
                        </li>
                        <li
                            className={adminTabClass("processingJobList")}
                            onClick={() => openAdminTab("processingJobList")}
                        >
                            {t("AdminPanel.ProcessingJobs")}
                        </li>
                        <li
                            className={adminTabClass("interforumFiles")}
                            onClick={() => openAdminTab("interforumFiles")}
                        >
                            {t("AdminPanel.InterforumFiles")}
                        </li>
                    </div>
                </ul>
            </div>
            <div className="admin-settings-container">
                <TabContext value={tabName}>
                    <TabPanel value="createCompany">
                        <h1>{t("AdminPanel.CreateCompany")}</h1>
                        <div className="settings-create">
                            <CreateCompanyPanel />
                        </div>
                    </TabPanel>
                    <TabPanel value="createUser">
                        <h1>{t("AdminPanel.CreateUser")}</h1>
                        <div className="settings-create">
                            <CreateUserPanel />
                        </div>
                    </TabPanel>
                    <TabPanel value="userList">
                        <h1>
                            {t("AdminPanel.ListUsers")} ({usersCount ?? 0})
                        </h1>
                        <div className="settings-list">
                            <UserListPanel />
                        </div>
                    </TabPanel>
                    <TabPanel value="companiesList">
                        <h1>
                            {t("AdminPanel.ListCompanies")} ({companies.length})
                        </h1>
                        <div className="settings-list">
                            <CompaniesListPanel />
                        </div>
                    </TabPanel>
                    <TabPanel value="companyGroups">
                        <h1>{t("AdminPanel.CompanyGroups")}</h1>
                        <div className="settings-list">
                            <CompanyGroupsPanel />
                        </div>
                    </TabPanel>
                    <TabPanel value="companyHiddenSettings">
                        <h1>{t("AdminPanel.HiddenSettings")}</h1>
                        <div className="settings-list">
                            <CompanyHiddenSettings />
                        </div>
                    </TabPanel>
                    <TabPanel value="maintenance">
                        <h1>{t("AdminPanel.Maintenance")}</h1>
                        <div className="settings-list">
                            <MaintenanceSchedule />
                        </div>
                    </TabPanel>
                    <TabPanel value="statementTranslation">
                        <h1>{t("AdminPanel.StatementI18n")}</h1>
                        <div className="settings-list">
                            <StatementI18n />
                        </div>
                    </TabPanel>
                    <TabPanel value="dangerousSettings">
                        <h1>{t("AdminPanel.DangerousSettings")}</h1>
                        <div className="settings-list">
                            <DangerousSettings />
                        </div>
                    </TabPanel>
                    <TabPanel value="logList">
                        <h1>{t("AdminPanel.LogList")}</h1>
                        <div className="settings-list">
                            <LogList />
                        </div>
                    </TabPanel>
                    <TabPanel value="processingJobList">
                        <h1>{t("AdminPanel.ProcessingJobs")}</h1>
                        <div className="settings-list">
                            <ImportTracking />
                        </div>
                    </TabPanel>
                    <TabPanel value="interforumFiles">
                        <h1>{t("AdminPanel.InterforumFiles")}</h1>
                        <div className="settings-list">
                            <InterforumFiles />
                        </div>
                    </TabPanel>
                </TabContext>
            </div>
        </div>
    );
};

export default AdminPanel;
